import type { ReactNode } from 'react';
import { Task, UpdateDocumentTaskMetadata } from '@/types/tasks.ts';
import { TASK_STEPS } from '@/context/house/tasks/utils.ts';

export const TOTAL_TASK_STEPS = TASK_STEPS.length;

/**
 * UI Task for immediate display in the interface
 * These tasks appear instantly when scheduled, before execution begins
 * All state (status, progress, result, error) comes from execution.apiTask
 */
export interface UITask {
	id: string;
	batchId: string;
	taskType: ConcurrentTaskType; // Task type for processing logic
	metadata: Record<string, unknown>; // Display metadata (filename, etc.)
	execution: ExecutionTask;
}

export interface TaskNotification {
	id?: string;
	// ReactNode, not string: the toaster renders descriptions as-is, which lets a
	// message link a word (e.g. "file" → the matched record). Plain strings remain
	// the common case.
	description: ReactNode;
	type: 'info' | 'error' | 'success';
	duration?: number;
	closable?: boolean;
}

/**
 * Execution task containing the execution function and API task state
 * This is what gets executed and tracked through the task lifecycle
 */
export interface ExecutionTask {
	apiTask: Task; // The task that will be tracked by TaskContext
	retryCount: number;
	/** How long this task may keep honouring rate-limit waits before it gives up. */
	maxRateLimitWaitMs: number;
	/** When it first began waiting on a rate limit; unset until it has been denied once. */
	retryingSince?: number;
	showNotification?: (notification: TaskNotification) => void;
	// Returns the created document id, or null when the content is already in the
	// Bundle and nothing was created — the task then settles as skipped.
	// `reportProgress` accepts whole percents for work the caller can measure,
	// which for an upload is bytes put on the wire. See TaskDefinition.
	executeFn: (signal?: AbortSignal, reportProgress?: ReportTaskProgress) => Promise<number | null>;
	onComplete?: () => Promise<void>; // Optional completion handler
	onAccepted?: () => Promise<void>; // Optional handler when task is accepted
}

/**
 * Reports how far along a task's own request is, in whole percent.
 *
 * Only covers work the client can observe. For an upload that is the transfer:
 * reaching 100 means the bytes have been sent, not that the server accepted
 * them or finished processing the document.
 */
export type ReportTaskProgress = (percent: number) => void;

// Task type constants for concurrent tasks
export const CONCURRENT_TASK_TYPES = {
	UPLOAD_RECORD: 'upload' as const,
	DELETE_RECORD: 'delete' as const,
	UPDATE_RECORD: 'update-record' as const,
} as const;
export type ConcurrentTaskType = (typeof CONCURRENT_TASK_TYPES)[keyof typeof CONCURRENT_TASK_TYPES];

/**
 * Task definition provided by consumers to schedule new tasks
 */
export interface TaskDefinition {
	id: string; // Unique identifier for deduplication
	taskType: ConcurrentTaskType; // Task type discrimination (upload or delete)
	metadata: Record<string, unknown> | UpdateDocumentTaskMetadata; // Display metadata
	// Created document id, or null = content already present (task settles skipped).
	executeFn: (signal?: AbortSignal, reportProgress?: ReportTaskProgress) => Promise<number | null>;
	onComplete?: () => Promise<void>; // Optional completion handler
	onAccepted?: () => Promise<void>; // Optional handler when task is accepted
	onBatchAccepted?: () => Promise<void>;
}

/**
 * How long a task keeps honouring rate-limit waits by default.
 *
 * Generous, because it bounds one task's own wait rather than a whole drop's. Files contend for
 * tokens, so a single file rarely waits more than a couple of minutes even in a large folder; ten
 * minutes is the point at which something is wrong rather than busy. A daily allowance is denied for
 * far longer than this, which is the intent — the task settles as paused and says so, instead of
 * holding the tab open for hours.
 */
export const DEFAULT_MAX_RATE_LIMIT_WAIT_MS = 10 * 60 * 1000;

/**
 * Batch options for scheduling multiple tasks together
 */
export interface BatchOptions {
	/**
	 * How long a task keeps honouring rate-limit waits before giving up, in milliseconds.
	 *
	 * Time rather than a count of attempts: a 429 is an appointment the server set, not an attempt
	 * that failed, so ten of them mean under a minute against a burst ceiling and over an hour
	 * against a daily allowance. A count measures how busy the server is, not how long this waited.
	 */
	maxRateLimitWaitMs?: number;
	showNotification?: (notification: TaskNotification) => void;
	onFailed?: () => Promise<void>;
	onComplete?: () => Promise<void>;
	/** Optional per-batch progress callback; called periodically as tasks finish */
	onProgress?: (progress: BatchProgress) => Promise<void>;
	/** How often to call onProgress; default used if not provided */
	progressEveryNthCompleted?: number;
}

export interface BatchProgress {
	completed: number;
	failed: number;
	skipped: number;
	cancelled: number;
	total: number;
}

/**
 * Status information about the task manager
 */
export interface TaskManagerStatus {
	totalTasks: number;
	queuedTasks: number;
	activeTasks: number;
	completedTasks: number;
	failedTasks: number;
	skippedTasks: number;
	cancelledTasks: number;
	totalLoad: number;
	canAcceptMore: boolean;
}
